import { useState } from 'react'
import { Icon } from '../../icons/Icon.jsx'
import { useWidgetContext, useWidgetState } from './context.js'
import { Card, PendingLine, SystemChip, asList } from './primitives.jsx'
import { scopeMessage } from './text.js'
import w from './widgets.module.css'

const rowsFrom = (pipes) =>
  asList(pipes).map((p) => ({
    key: p.key ?? null,
    name: p.name ?? '',
    direction: p.direction ?? '',
    complexity: p.complexity ?? '',
    on: p.default_on !== false,
    custom: false,
    values: {},
  }))

let nextColumn = 1

/**
 * #6 Scope picker (pick_scope). The user takes pipes out (✕) or adds them back,
 * adds their own pipes and columns, then "Use this scope" sends the scope as
 * the next question. The
 * picked scope is saved so the card shows it read-only afterwards.
 */
export function ScopePicker({ input, block, reply, canAnswer }) {
  const { onAsk } = useWidgetContext()
  const [saved, save] = useWidgetState(block.key, null)
  const [edited, setEdited] = useState(null)
  const [columns, setColumns] = useState([])
  const [focus, setFocus] = useState(null)

  const systems = asList(input.systems)
  const rows = edited ?? rowsFrom(input.pipes)
  const directions = [...new Set([...rows.map((r) => r.direction).filter(Boolean), 'Both'])]
  const title = 'Which pipes are in scope?'
  const sub = systems.length ? (
    <span className={w.rowTight}>
      {systems.map((slug, i) => (
        <span key={slug} className={w.rowTight}>
          {i ? <span aria-hidden="true">↔</span> : null}
          <SystemChip slug={slug} />
        </span>
      ))}
    </span>
  ) : null

  if (reply || saved?.pipes) {
    const picked = asList(saved?.pipes).filter((p) => p.on)
    const cols = asList(saved?.columns)
    return (
      <Card
        label="Scope"
        title="Scope"
        sub={sub}
        right={
          picked.length ? (
            <span className={w.muted}>{`${picked.length} ${picked.length === 1 ? 'pipe' : 'pipes'}`}</span>
          ) : null
        }
      >
        {picked.length ? (
          <div className={w.scrollX}>
            <table className={w.table}>
              <thead>
                <tr>
                  <th scope="col">Pipe</th>
                  <th scope="col">Direction</th>
                  {cols.map((c) => (
                    <th key={c.id} scope="col">
                      {c.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {picked.map((p, i) => (
                  <tr key={`${p.name}-${i}`}>
                    <td>
                      {p.name}
                      {p.custom ? <span className={w.cellNote}>Added by you · no skill coverage yet</span> : null}
                    </td>
                    <td className={w.muted}>{p.direction}</td>
                    {cols.map((c) => (
                      <td key={c.id} className={w.muted}>
                        {p.values?.[c.id] || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={w.muted}>
            <Icon name="check" size={13} strokeWidth={1.8} /> Answered: {reply}
          </div>
        )}
      </Card>
    )
  }

  const editable = canAnswer && block.complete
  const update = (fn) => setEdited((current) => fn(current ?? rowsFrom(input.pipes)))
  const setRow = (i, patch) => update((list) => list.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const addPipe = () => {
    update((list) => [
      ...list,
      {
        key: null,
        name: '',
        direction: directions[0] ?? 'Both',
        complexity: 'To assess',
        on: true,
        custom: true,
        values: {},
      },
    ])
    setFocus(`pipe-${rows.length}`)
  }
  const addColumn = () => {
    const column = { id: `c${nextColumn++}`, name: 'New column' }
    setColumns((list) => [...list, column])
    setFocus(`col-${column.id}`)
  }
  const autoFocus = (key) => (el) => {
    if (el && focus === key) {
      el.focus()
      el.select?.()
      setFocus(null)
    }
  }
  const selected = rows.filter((r) => r.on && r.name.trim()).length
  // Pipes taken out of scope leave the table and wait below as "+ name".
  const leftOut = rows.map((r, i) => ({ r, i })).filter(({ r }) => !r.on && !r.custom)
  const remove = (i) => (rows[i].custom ? update((list) => list.filter((_, j) => j !== i)) : setRow(i, { on: false }))

  const submit = () => {
    const scope = { pipes: rows, columns }
    save(scope)
    onAsk?.(scopeMessage(rows, columns))
  }

  return (
    <Card label={title} title={title} sub={sub}>
      {rows.length ? (
        <div className={w.scrollX}>
          <table className={w.table}>
            <thead>
              <tr>
                <th scope="col">Pipe</th>
                <th scope="col">Direction</th>
                <th scope="col">Complexity</th>
                {columns.map((c) => (
                  <th key={c.id} scope="col">
                    <span className={w.rowTight} style={{ flexWrap: 'nowrap' }}>
                      <input
                        ref={autoFocus(`col-${c.id}`)}
                        className={w.thInput}
                        value={c.name}
                        aria-label="Column name"
                        disabled={!editable}
                        onChange={(e) =>
                          setColumns((list) => list.map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)))
                        }
                      />
                      <button
                        type="button"
                        className={w.miniBtn}
                        aria-label={`Remove column ${c.name}`}
                        disabled={!editable}
                        onClick={() => setColumns((list) => list.filter((x) => x.id !== c.id))}
                      >
                        ✕
                      </button>
                    </span>
                  </th>
                ))}
                <th>
                  {editable ? (
                    <button
                      type="button"
                      className={`btn btn-ghost ${w.btnSm} ${w.btnGhostDashed}`}
                      onClick={addColumn}
                    >
                      + Add column
                    </button>
                  ) : null}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) =>
                r.on ? (
                  <tr key={i} className={r.custom ? w.customRow : undefined}>
                    <td>
                      {r.custom ? (
                        <input
                          ref={autoFocus(`pipe-${i}`)}
                          className={w.cellInput}
                          value={r.name}
                          placeholder="Pipe name"
                          onChange={(e) => setRow(i, { name: e.target.value })}
                        />
                      ) : (
                        r.name
                      )}
                    </td>
                    <td>
                      {r.custom ? (
                        <select
                          className={w.select}
                          value={r.direction}
                          onChange={(e) => setRow(i, { direction: e.target.value })}
                        >
                          {directions.map((d) => (
                            <option key={d}>{d}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={w.muted}>{r.direction}</span>
                      )}
                    </td>
                    <td className={w.muted}>{r.complexity}</td>
                    {columns.map((c) => (
                      <td key={c.id}>
                        <input
                          className={w.cellInput}
                          value={r.values[c.id] ?? ''}
                          placeholder="—"
                          disabled={!editable}
                          aria-label={`${c.name} for ${r.name || 'pipe'}`}
                          onChange={(e) => setRow(i, { values: { ...r.values, [c.id]: e.target.value } })}
                        />
                      </td>
                    ))}
                    <td>
                      {editable ? (
                        <button
                          type="button"
                          className={w.miniBtn}
                          aria-label={`Take ${r.name || 'this pipe'} out of scope`}
                          onClick={() => remove(i)}
                        >
                          ✕
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ) : null,
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <PendingLine />
      )}
      {leftOut.length ? (
        <div className={w.rowTight}>
          <span className={w.muted}>Not in scope:</span>
          {leftOut.map(({ r, i }) => (
            <button
              key={i}
              type="button"
              className={w.pill}
              disabled={!editable}
              aria-label={`Add ${r.name} back to scope`}
              onClick={() => setRow(i, { on: true })}
            >
              + {r.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className={w.row}>
        {editable ? (
          <button type="button" className={`btn btn-ghost ${w.btnSm} ${w.btnGhostDashed}`} onClick={addPipe}>
            + Add pipe
          </button>
        ) : null}
        <span className={w.muted} style={{ marginLeft: 'auto' }}>
          {selected} {selected === 1 ? 'pipe' : 'pipes'} in scope
        </span>
        <button type="button" className="btn btn-dark" disabled={!editable || !selected} onClick={submit}>
          Use this scope
        </button>
      </div>
    </Card>
  )
}
